import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

type CalculationMethod = "standar" | "japanese" | "commutative" | "pascal";

const MultiplicationCalculator = () => {
  const [num1, setNum1] = useState("");
  const [num2, setNum2] = useState("");
  const [result, setResult] = useState<number | null>(null);
  const [steps, setSteps] = useState<string[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<CalculationMethod>("standar");

  const calculateWithSteps = () => {
    const n1_calc = parseInt(num1);
    const n2_calc = parseInt(num2);

    if (isNaN(n1_calc) || isNaN(n2_calc)) {
      setResult(null);
      setSteps([]);
      return;
    }

    let calculationResult: { result: number; steps: string[] } | null = null;

    switch (selectedMethod) {
      case "standar":
        calculationResult = calculateStandar(n1_calc, n2_calc);
        break;
      case "japanese":
        calculationResult = calculateJapanese(n1_calc, n2_calc);
        break;
      case "commutative":
        calculationResult = calculateCommutative(n1_calc, n2_calc);
        break;
      case "pascal":
        calculationResult = calculatePascal(n1_calc, n2_calc);
        break;
      default:
        calculationResult = calculateStandar(n1_calc, n2_calc); // Fallback
        break;
    }

    if (calculationResult) {
      setResult(calculationResult.result);
      setSteps(calculationResult.steps);
    } else {
      // Fallback if a method isn't fully implemented or fails unexpectedly
      const res = n1_calc * n2_calc;
      setResult(res);
      setSteps([`${n1_calc} × ${n2_calc} = ${res}`]);
    }
  };

  const calculateStandar = (n1: number, n2: number): { result: number; steps: string[] } => {
    const res = n1 * n2;
    const generatedSteps: string[] = [];
    if (n1 < 100 && n2 < 100 && n1 > 9 && n2 > 9) { // Two-digit specific steps
      const n1_tens = Math.floor(n1 / 10);
      const n1_ones = n1 % 10;
      const n2_tens = Math.floor(n2 / 10);
      const n2_ones = n2 % 10;
      generatedSteps.push(`${n1} × ${n2}`);
      generatedSteps.push(`= (${n1_tens}0 + ${n1_ones}) × (${n2_tens}0 + ${n2_ones})`);
      const step1_val = n1_ones * n2_ones;
      const step2_val = n1_ones * (n2_tens * 10);
      const step3_val = (n1_tens * 10) * n2_ones;
      const step4_val = (n1_tens * 10) * (n2_tens * 10);
      generatedSteps.push(`= (${n1_ones} × ${n2_ones}) + (${n1_ones} × ${n2_tens}0) + (${n1_tens}0 × ${n2_ones}) + (${n1_tens}0 × ${n2_tens}0)`);
      generatedSteps.push(`= ${step1_val} + ${step2_val} + ${step3_val} + ${step4_val}`);
      generatedSteps.push(`= ${res}`);
    } else {
      generatedSteps.push(`${n1} × ${n2} = ${res}`);
    }
    return { result: res, steps: generatedSteps };
  };

  const calculateJapanese = (n1: number, n2: number): { result: number; steps: string[] } => {
    const res = n1 * n2;
    const generatedSteps: string[] = [];
    if (n1 < 1 || n1 > 99 || n2 < 1 || n2 > 99) {
      generatedSteps.push("Metode Garis Jepang paling cocok untuk angka 1 atau 2 digit.");
      generatedSteps.push(`${n1} × ${n2} = ${res}`);
      return { result: res, steps: generatedSteps };
    }
    const s1_digits = String(n1).split('').map(Number);
    const s2_digits = String(n2).split('').map(Number);
    generatedSteps.push(`Metode Garis Jepang untuk ${n1} × ${n2}:`);
    generatedSteps.push(`1. Gambar garis untuk angka pertama (${n1}):`);
    s1_digits.forEach((digit, index) => {
      generatedSteps.push(`   - ${digit} garis ${s1_digits.length > 1 && index === 0 ? 'untuk puluhan (atas)' : (s1_digits.length > 1 && index === 1 ? 'untuk satuan (bawah)' : 'untuk satuan')}`);
    });
    generatedSteps.push(`2. Gambar garis untuk angka kedua (${n2}) melintasi garis pertama (dari kiri ke kanan):`);
    s2_digits.forEach((digit, index) => {
      generatedSteps.push(`   - ${digit} garis ${s2_digits.length > 1 && index === 0 ? 'untuk puluhan (kiri)' : (s2_digits.length > 1 && index === 1 ? 'untuk satuan (kanan)' : 'untuk satuan')}`);
    });
    generatedSteps.push("3. Hitung titik persimpangan dari kanan ke kiri:");

    if (s1_digits.length === 1 && s2_digits.length === 1) { // 1x1 digit
      const ones = s1_digits[0] * s2_digits[0];
      generatedSteps.push(`   - Zona satuan (kanan): ${s1_digits[0]} × ${s2_digits[0]} = ${ones} titik`);
      generatedSteps.push(`Hasil: ${res}`);
    } else if (s1_digits.length === 2 && s2_digits.length === 1) { // 2x1 digit (e.g., 21 x 3)
      const ones_val = s1_digits[1] * s2_digits[0]; // satuan N1 x satuan N2
      const tens_val = s1_digits[0] * s2_digits[0]; // puluhan N1 x satuan N2
      generatedSteps.push(`   - Zona satuan (kanan bawah × kanan): ${s1_digits[1]} × ${s2_digits[0]} = ${ones_val} titik`);
      generatedSteps.push(`   - Zona puluhan (kiri atas × kanan): ${s1_digits[0]} × ${s2_digits[0]} = ${tens_val} titik`);
      generatedSteps.push("4. Gabungkan hasil:");
      generatedSteps.push(`   Satuan: ${ones_val % 10} (simpanan puluhan: ${Math.floor(ones_val / 10)})`);
      const total_tens = tens_val + Math.floor(ones_val / 10);
      generatedSteps.push(`   Puluhan: ${total_tens}`);
      generatedSteps.push(`Hasil: ${res}`);
    } else if (s1_digits.length === 1 && s2_digits.length === 2) { // 1x2 digit (e.g., 3 x 21)
      const ones_val = s1_digits[0] * s2_digits[1]; // satuan N1 x satuan N2
      const tens_val = s1_digits[0] * s2_digits[0]; // satuan N1 x puluhan N2
      generatedSteps.push(`   - Zona satuan (kanan × kanan bawah): ${s1_digits[0]} × ${s2_digits[1]} = ${ones_val} titik`);
      generatedSteps.push(`   - Zona puluhan (kanan × kiri atas): ${s1_digits[0]} × ${s2_digits[0]} = ${tens_val} titik`);
      generatedSteps.push("4. Gabungkan hasil:");
      generatedSteps.push(`   Satuan: ${ones_val % 10} (simpanan puluhan: ${Math.floor(ones_val / 10)})`);
      const total_tens = tens_val + Math.floor(ones_val / 10);
      generatedSteps.push(`   Puluhan: ${total_tens}`);
      generatedSteps.push(`Hasil: ${res}`);
    } else if (s1_digits.length === 2 && s2_digits.length === 2) { // 2x2 digit (e.g., 21 x 13)
      const ones_intersections = s1_digits[1] * s2_digits[1];     // satuan N1 x satuan N2 (kanan bawah)
      const tens_intersections_a = s1_digits[0] * s2_digits[1];   // puluhan N1 x satuan N2 (atas kanan)
      const tens_intersections_b = s1_digits[1] * s2_digits[0];   // satuan N1 x puluhan N2 (bawah kiri)
      const hundreds_intersections = s1_digits[0] * s2_digits[0]; // puluhan N1 x puluhan N2 (kiri atas)
      generatedSteps.push(`   - Zona satuan (kanan bawah): ${s1_digits[1]} (garis satuan N1) × ${s2_digits[1]} (garis satuan N2) = ${ones_intersections} titik`);
      generatedSteps.push(`   - Zona puluhan (diagonal tengah):`);
      generatedSteps.push(`     (${s1_digits[0]} (garis puluhan N1) × ${s2_digits[1]} (garis satuan N2)) + (${s1_digits[1]} (garis satuan N1) × ${s2_digits[0]} (garis puluhan N2))`);
      generatedSteps.push(`     = ${tens_intersections_a} + ${tens_intersections_b} = ${tens_intersections_a + tens_intersections_b} titik`);
      generatedSteps.push(`   - Zona ratusan (kiri atas): ${s1_digits[0]} (garis puluhan N1) × ${s2_digits[0]} (garis puluhan N2) = ${hundreds_intersections} titik`);
      generatedSteps.push("4. Gabungkan hasil (dari kanan ke kiri, perhatikan simpanan):");
      const ones_digit = ones_intersections % 10;
      const carry_to_tens = Math.floor(ones_intersections / 10);
      generatedSteps.push(`   Satuan: ${ones_digit} (simpanan puluhan: ${carry_to_tens})`);
      const total_tens_value = tens_intersections_a + tens_intersections_b + carry_to_tens;
      const tens_digit = total_tens_value % 10;
      const carry_to_hundreds = Math.floor(total_tens_value / 10);
      generatedSteps.push(`   Puluhan: ${tens_digit} (simpanan ratusan: ${carry_to_hundreds})`);
      const total_hundreds_value = hundreds_intersections + carry_to_hundreds;
      generatedSteps.push(`   Ratusan: ${total_hundreds_value}`);
      generatedSteps.push(`Hasil: ${res}`);
    }
    return { result: res, steps: generatedSteps };
  };

  const calculateCommutative = (n1: number, n2: number): { result: number; steps: string[] } => {
    const res = n1 * n2;
    const generatedSteps: string[] = [
      `Metode Sifat Komutatif untuk ${n1} × ${n2}:`,
      `Sifat komutatif perkalian menyatakan: a × b = b × a.`,
      `Jadi, ${n1} × ${n2} sama dengan ${n2} × ${n1}.`,
      `Hasilnya adalah: ${res}`,
    ];
    if (n1 !== n2) {
      generatedSteps.push(`Anda bisa memilih menghitung ${n1} × ${n2} atau ${n2} × ${n1}, mana yang dirasa lebih mudah.`);
    }
    return { result: res, steps: generatedSteps };
  };

  const calculatePascal = (n1: number, n2: number): { result: number; steps: string[] } => {
    const res = n1 * n2;
    const generatedSteps: string[] = [];
    generatedSteps.push(`Metode Segitiga Pascal (Bentuk Binomial) untuk ${n1} × ${n2}:`);
    generatedSteps.push(`Menggunakan identitas: A × B = ((A+B)/2)² - ((A-B)/2)²`);
    if ((n1 % 2 !== n2 % 2)) { // Check if numbers have different parity
      generatedSteps.push("Catatan: Metode ini paling mudah jika kedua angka (A dan B) sama-sama genap atau sama-sama ganjil. Jika paritasnya berbeda, langkah perantara akan melibatkan pecahan.");
    }
    const term1_sum = n1 + n2;
    const term1_avg = term1_sum / 2;
    const term2_diff = n1 - n2;
    const term2_avg = term2_diff / 2;
    generatedSteps.push(`1. A = ${n1}, B = ${n2}`);
    generatedSteps.push(`2. (A+B)/2 = (${n1}+${n2})/2 = ${term1_sum}/2 = ${term1_avg}`);
    generatedSteps.push(`3. (A-B)/2 = (${n1}-${n2})/2 = ${term2_diff}/2 = ${term2_avg}`);
    const term1_squared = Math.pow(term1_avg, 2);
    const term2_squared = Math.pow(term2_avg, 2);
    generatedSteps.push(`4. Hasil = (${term1_avg})² - (${term2_avg})²`);
    generatedSteps.push(`   = ${term1_squared} - ${term2_squared}`);
    generatedSteps.push(`   = ${res}`);
    return { result: res, steps: generatedSteps };
  };

  const clear = () => {
    setNum1("");
    setNum2("");
    setResult(null);
    setSteps([]);
    setSelectedMethod("standar");
  };

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-2xl mx-auto bg-white shadow-xl rounded-lg">
      <div>
        <h3 className="text-xl font-semibold mb-4 text-center text-gray-700">Pilih Metode Perkalian:</h3>
        <RadioGroup
          value={selectedMethod}
          onValueChange={(value) => setSelectedMethod(value as CalculationMethod)}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          {[ 
            { value: "standar", label: "Standar" },
            { value: "japanese", label: "Garis Jepang" },
            { value: "commutative", label: "Sifat Komutatif" },
            { value: "pascal", label: "Segitiga Pascal" },
          ].map((method) => (
            <div
              key={method.value}
              className="flex items-center space-x-2 p-3 border rounded-md hover:bg-indigo-50 transition-colors duration-150 cursor-pointer has-[:checked]:bg-indigo-100 has-[:checked]:border-indigo-500"
              onClick={() => setSelectedMethod(method.value as CalculationMethod)}
            >
              <RadioGroupItem value={method.value} id={method.value} className="text-indigo-600" />
              <Label htmlFor={method.value} className="cursor-pointer flex-1 text-sm font-medium text-gray-600 select-none">
                {method.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="num1" className="text-sm font-medium block mb-1.5 text-gray-600">Angka Pertama:</Label>
            <Input
              id="num1"
              type="number"
              value={num1}
              onChange={(e) => setNum1(e.target.value)}
              placeholder="e.g., 23"
              className="w-full p-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <Label htmlFor="num2" className="text-sm font-medium block mb-1.5 text-gray-600">Angka Kedua:</Label>
            <Input
              id="num2"
              type="number"
              value={num2}
              onChange={(e) => setNum2(e.target.value)}
              placeholder="e.g., 12"
              className="w-full p-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button onClick={calculateWithSteps} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 px-4 rounded-md transition-colors duration-150">
            Hitung Perkalian
          </Button>
          <Button onClick={clear} variant="outline" className="flex-1 sm:flex-none border-gray-300 text-gray-700 hover:bg-gray-100 font-semibold py-2.5 px-4 rounded-md transition-colors duration-150">
            Reset
          </Button>
        </div>

        {result !== null && (
          <Card className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 shadow-lg mt-6">
            <CardHeader>
              <CardTitle className="text-center text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 py-2">
                Hasil: {result}
              </CardTitle>
            </CardHeader>
            {steps.length > 0 && !(selectedMethod === 'standar' && steps.length === 1 && steps[0] === `${parseInt(num1) || 0} × ${parseInt(num2) || 0} = ${result}`) && (
              <CardContent className="pt-2 pb-4 px-4 md:px-6">
                <h4 className="font-semibold text-lg md:text-xl mb-2.5 text-gray-700">Langkah-langkah ({selectedMethod.charAt(0).toUpperCase() + selectedMethod.slice(1).replace('-', ' ')}):</h4>
                <div className="space-y-2 bg-white/70 p-3 md:p-4 rounded-md shadow-inner">
                  {steps.map((step, index) => (
                    <div key={index} className="text-sm font-mono whitespace-pre-wrap break-words text-gray-800 leading-relaxed">
                      {step}
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default MultiplicationCalculator;